import shopService from "../../service/shop.service";
import {getSbpPayUrl} from "../../utils/payUrlGenerator"
import {
   checkoutVisibleChanged,
   orderChanged,
   regionChanged,
   orderReceived,
   regionListRequested,
   regionListReceived,
   errorAdded,
   warningAdded,
   orderStatusChanged,
   orderRequested,
   billGenerated,
   activeShopChanged,
   errorCleared,
} from "./orderSlice";

/** Ключ для хранения глобального состояния видимости чекаута в sessionStorage */
const CHECKOUT_STORAGE_KEY = 'checkoutVisible';

/**
 * Парсит строковое значение из хранилища в булево значение.
 * @param {string} raw - Строковое значение из хранилища.
 * @returns {boolean|null} - Возвращает true, false или null если значение не определено.
 */
const parseBooleanFromStorage = (raw) => {
   if (raw === "true") return true;
   if (raw === "false") return false;
   return null;
};


/**
 * Устанавливает активный магазин.
 * @param {string} shop - Идентификатор магазина.
 * @returns {Function} - Thunk функция для Redux.
 */
export const changeShop = (shop) => async (dispatch) => {
    dispatch(activeShopChanged({ shop }));
}

/**
 * Получает список регионов для указанного магазина.
 * Загружает список регионов с сервера и сохраняет их в store.
 * При успешной загрузке автоматически выбирает первый регион из списка.
 * @param {string} shop - Идентификатор магазина (например, 'eshop', 'psn', 'steam').
 * @returns {Function} - Thunk функция для Redux.
 */
export const receiveRegionList = (shop) => async (dispatch, getState) => {
   dispatch(regionListRequested({ shop }));
   try {
      const { regionList } = await shopService.getRegionsByShopName(shop);
      if (!regionList || regionList.length === 0) {
        dispatch(regionListReceived({ shop, regionList: [] }));
        dispatch(regionChanged({ shop, region: null }));
        return null;
      }
      dispatch(regionListReceived({ shop, regionList }));
      const storedRegion = getState().order.shops[shop]?.currentRegion;
      const regionExists = storedRegion && regionList.some((r) => r.cardRegionCd === storedRegion);
      const currentRegion = regionExists ? storedRegion : regionList[0].cardRegionCd;
      if (!regionExists) {
         dispatch(regionChanged({ shop, region: currentRegion }));
      }
      dispatch(receiveOrderByShopAndRegion(shop, { params: { regionType: currentRegion } }));
      return currentRegion;
   } catch (error) {
      const message = error?.response?.data?.message ? error?.response?.data?.message : error?.message;
      const status = error?.response?.status || null;
      dispatch(regionListReceived({ shop, regionList: [] }));
      dispatch(regionChanged({ shop, region: null }));
      dispatch(errorAdded({ shop, message, status }));
      return null;
   }
}


/**
 * Получает данные заказа по магазину и региону.
 * Загружает информацию о заказе с сервера и сохраняет её в store.
 * Перед загрузкой устанавливает состояние загрузки.
 * @param {string} shop - Идентификатор магазина.
 * @param {Object} params - Параметры запроса (например, регион, идентификаторы).
 * @returns {Function} - Thunk функция для Redux.
 */
export const receiveOrderByShopAndRegion = (shop, params) => async (dispatch) => {
   dispatch(orderRequested({ shop }));
   try {
      const data = await shopService.receiveOrderByShopAndRegion(shop, params);
      dispatch(orderReceived({ shop, data }));
   } catch (error) {
      const message = error?.response?.data?.message ? error?.response?.data?.message : error?.message;
      const status = error?.response?.status || null;
      dispatch(errorAdded({ shop, message, status }));
   }
};

/**
 * Инициализирует глобальное состояние видимости чекаута.
 * Загружает значение из sessionStorage или использует fallback значение.
 * Применяется ко всем магазинам одновременно.
 * @param {boolean} [fallback=true] - Значение по умолчанию, если в хранилище нет данных.
 * @returns {Function} - Thunk функция для Redux.
 */
export const initCheckoutVisible = (fallback = true) => (dispatch) => {
   const stored = parseBooleanFromStorage(
      sessionStorage.getItem(CHECKOUT_STORAGE_KEY)
   );
   const value = stored ?? fallback;
   sessionStorage.setItem(CHECKOUT_STORAGE_KEY, String(value));
   dispatch(checkoutVisibleChanged({ value }));
};

/**
 * Устанавливает глобальную видимость чекаута.
 * Сохраняет значение в sessionStorage и обновляет состояние в store.
 * Применяется ко всем магазинам одновременно.
 * @param {boolean} value - Новое значение видимости чекаута.
 * @returns {Function} - Thunk функция для Redux.
 */
export const setCheckoutVisible = (value) => (dispatch) => {
   sessionStorage.setItem(CHECKOUT_STORAGE_KEY, String(value));
   dispatch(checkoutVisibleChanged({ value }));
};

/**
 * Изменяет данные заказа.
 * Отправляет запрос на сервер для обновления заказа и сохраняет обновленные данные в store.
 * @param {Object} body - Данные для изменения заказа (например, товары, количество, shopType).
 * @returns {Function} - Thunk функция для Redux.
 */
export const changeOrder = (body) => async (dispatch) => {
   const shop = body?.shopType;
   try {
      const data = await shopService.changeOrder(body);
      dispatch(orderChanged({ shop, data }));
   } catch (error) {
      const message = error?.response?.data?.message ? error?.response?.data?.message : error?.message;
      const status = error?.response?.status || null;
      dispatch(warningAdded({ shop, message, status }));
   }
};

/**
 * Генерирует счёт для заказа и открывает ссылку оплаты СБП.
 * Создаёт счёт для оплаты через систему быстрых платежей (СБП).
 * Если в ответе содержится payUrl, открывает его в новой вкладке браузера.
 * @param {Object} body - Данные для генерации счёта (детали заказа, shopType).
 * @returns {Function} - Thunk функция для Redux. Возвращает данные сгенерированного счёта при успехе.
 */
export const generateBillForOrder = (body) => async (dispatch) => {
   const shop = body?.shopType;
   try {
      const data = await shopService.generateBill(body);
      dispatch(billGenerated({ shop, payUrl: data.payUrl }));
      if (data.payUrl) {
         window.open(getSbpPayUrl(data.payUrl), '_blank', 'noreferrer');
      }
      return data;
   } catch (error) {
      const message = error?.response?.data?.message ? error?.response?.data?.message : error?.message;
      const status = error?.response?.status || null;
      dispatch(warningAdded({ shop, message, status }));
   }
};

/**
 * Получает текущий статус заказа.
 * Запрашивает статус заказа с сервера и обновляет его в store.
 * @param {Object} params - Параметры запроса (идентификатор заказа, shopType).
 * @returns {Function} - Thunk функция для Redux. Возвращает данные статуса заказа при успехе.
 */
export const receiveOrderStatus = (params) => async (dispatch) => {
    const shop = params?.shopType;
    try {
       const data = await shopService.receiveOrderStatus(params);
       dispatch(orderStatusChanged({ shop, data }));
       return data;
    } catch (error) {
       const message = error?.response?.data?.message ? error?.response?.data?.message : error?.message;
       const status = error?.response?.status || null;
       dispatch(errorAdded({ shop, message, status }));
    }
};

/**
 * Очищает ошибку для указанного магазина.
 * @param {string} shop - Идентификатор магазина.
 * @returns {Function} - Thunk функция для Redux.
 */
export const clearError = (shop) => (dispatch) => {
    dispatch(errorCleared({ shop }));
}
